import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Link, Typography } from '@mui/material';
import { first, map, size, uniqBy } from 'lodash';
import pluralize from 'pluralize';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { CommonJobResult } from 'interfaces/job';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';

interface Props {
  studyId?: string;
  links: CommonJobResult['links'];
}

export const LinksSection: React.FC<React.PropsWithChildren<Props>> = ({ links, studyId }) => {
  const { labId } = useCurrentLabId();

  const { getUrlToCasePage, getUrlToSlidePage } = useNavigationToViewerPage();
  const slideLinks = uniqBy(links?.slides, 'id');
  const caseLinks = uniqBy(links?.cases, 'id');
  const slidesNum = size(slideLinks);
  const casesNum = size(caseLinks);

  return slidesNum + casesNum == 1 ? (
    <Grid item>
      <Typography variant="caption">
        Related to{' '}
        <Link
          to={
            slidesNum > 0
              ? getUrlToSlidePage({ slideId: first(slideLinks)?.id, labId, caseStudyId: studyId })
              : getUrlToCasePage({ caseId: first(caseLinks)?.id, labId, caseStudyId: studyId })
          }
          target="_blank"
          rel="noopener noreferrer"
          component={RouterLink}
        >
          {(slidesNum > 0 ? first(slideLinks) : first(caseLinks))?.display_name ??
            (slidesNum > 0 ? first(slideLinks)?.id : first(caseLinks)?.id)}
        </Link>
      </Typography>
    </Grid>
  ) : (
    slidesNum + casesNum > 1 && (
      <Accordion variant="outlined" sx={{ width: '100%' }} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Grid item>
            <Typography variant="caption">
              Related to {slidesNum > 0 && `${slidesNum} ${pluralize('slide', slidesNum)}`}{' '}
              {casesNum > 0 && `and ${casesNum} ${pluralize('case', casesNum)}`}
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid item container spacing={1} margin={0} direction="column">
            {slidesNum > 0 && (
              <>
                {casesNum > 0 && (
                  <Grid item>
                    <Typography variant="caption">Slides:</Typography>
                  </Grid>
                )}
                {map(slideLinks, (slide) => (
                  <Grid item key={slide.id}>
                    <Link
                      to={getUrlToSlidePage({ slideId: slide.id, labId, caseStudyId: studyId })}
                      target="_blank"
                      rel="noopener noreferrer"
                      component={RouterLink}
                    >
                      <Typography variant="caption" key={slide.id}>
                        {slide.display_name || slide.id}
                      </Typography>
                    </Link>
                  </Grid>
                ))}
              </>
            )}
            {casesNum > 0 && (
              <>
                {slidesNum > 0 && (
                  <Grid item>
                    <Typography variant="caption">Cases:</Typography>
                  </Grid>
                )}
                {map(caseLinks, (caseEntry) => (
                  <Grid item key={caseEntry.id}>
                    <Link
                      to={getUrlToCasePage({ caseId: caseEntry.id, labId, caseStudyId: studyId })}
                      target="_blank"
                      rel="noopener noreferrer"
                      component={RouterLink}
                    >
                      <Typography variant="caption" key={caseEntry.id}>
                        {caseEntry.display_name || caseEntry.id}
                      </Typography>
                    </Link>
                  </Grid>
                ))}
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  );
};
